import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import { theme } from '../../../styles/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faCalendar, faCheck, faTimes, faCalendarAlt, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import SEO from '../../common/SEO';
import {
  ANIMAL_PARK_OCTOBER_HALF_TERM_2026,
  getAnimalParkOpeningDisplay,
  getCastleOpeningDisplay,
  isCastleOpenAllDaysThisWeek,
  isCastleClosedTuesdayOrWednesday,
  isCastleClosedUntilChristmas,
} from '../../../utils/openingHoursUtils';
const gardensImage = 'https://BelvoirCastle.b-cdn.net/images/gardens.webp';

// Import images
const retailImage = 'https://BelvoirCastle.b-cdn.net/Retail%20village/retail.webp';

const castleImage = 'https://BelvoirCastle.b-cdn.net/Castle/castle2.webp';

const OpeningTimesContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
  text-align: center;
`;

const IntroText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  max-width: 800px;
  margin: 0 auto 3rem;
  text-align: center;
`;

const TimesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const VenueCard = styled.div`
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`;

const StatusBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  background-color: ${props => {
    if (props.$isOpen) return 'rgba(76, 175, 80, 0.85)';
    if (props.$openingSoon) return 'rgba(255, 165, 0, 0.85)';
    return 'rgba(244, 67, 54, 0.85)';
  }};
  color: white;
  font-weight: 500;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  backdrop-filter: blur(4px);
`;

const VenueImage = styled.div`
  height: 200px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  position: relative;
`;

const VenueContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const VenueName = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  min-height: 60px; /* Ensure consistent height for title area */
`;

const TimeInfo = styled.div`
  margin-bottom: 1.5rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.3rem;
`;

const DayLabel = styled.div`
  color: ${props => props.theme.colors.secondary};
  font-size: 1rem;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 1px;
`;

const Hours = styled.div`
  color: ${props => props.theme.colors.primary};
  font-size: 1.4rem;
  font-weight: 500;
`;

const Note = styled.div`
  color: ${props => props.theme.colors.primary};
  font-size: 0.9rem;
  font-style: italic;
  margin-top: 0.3rem;
`;

const VenueDescription = styled.p`
  flex-grow: 1;
  margin-top: 0.5rem;
  line-height: 1.6;
`;

const CurrentTimeDisplay = styled.div`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 2rem;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 500;
`;

const CTASection = styled.div`
  margin: 5rem 0;
  display: flex;
  gap: 2rem;
  justify-content: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const CTACard = styled.div`
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-width: 400px;
  width: 100%;
`;

const CTATitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const CTADescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.primary};
`;

const CTAButton = styled.a`
  display: inline-block;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 1rem 2rem;
  border: 2px solid transparent;
  border-radius: 25px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
  }
`;

const OpeningPageNotice = styled.div`
  background-color: #fef9f3;
  border-left: 4px solid ${props => props.theme.colors.accent};
  border-radius: 0 8px 8px 0;
  padding: 1.25rem 1.5rem;
  margin-bottom: 2rem;
  font-size: 1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.primary};

  h3 {
    font-family: ${props => props.theme.fonts.main};
    font-size: 1.25rem;
    margin: 0 0 0.5rem;
    color: ${props => props.theme.colors.primary};
  }
  p {
    margin: 0 0 0.75rem;
  }
  p:last-child {
    margin-bottom: 0;
  }
  strong {
    color: ${props => props.theme.colors.primary};
  }
`;

const ViewToggle = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;
  align-items: center;
`;

const ToggleButton = styled.button`
  background-color: ${props => props.$active ? props.theme.colors.accent : 'transparent'};
  color: ${props => props.$active ? 'white' : props.theme.colors.accent};
  padding: 0.8rem 1.5rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  border: 2px solid ${props => props.theme.colors.accent};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background-color: ${props => props.$active ? props.theme.colors.accent : 'rgba(0, 0, 0, 0.05)'};
  }
`;

const EventsLink = styled.button`
  background-color: transparent;
  color: ${props => props.theme.colors.accent};
  padding: 0.8rem 1.5rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  border: 2px solid ${props => props.theme.colors.accent};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;

  &:hover {
    background-color: ${props => props.theme.colors.accent};
    color: white;
  }
`;

const CalendarContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const MonthContainer = styled.div`
  margin-bottom: 3rem;
`;

const MonthTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  color: ${props => props.theme.colors.primary};
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
`;

const DayHeader = styled.div`
  text-align: center;
  font-weight: 600;
  padding: 0.5rem;
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 4px;
`;

const DayCell = styled.div`
  padding: 1rem;
  text-align: center;
  border-radius: 4px;
  background-color: ${props => props.theme.colors.greyBackground};
  border: 1px solid ${props => props.theme.colors.greyBackground};
  min-height: 120px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;
`;

const EventIndicator = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 0.2rem;
  background-color: ${props => {
    if (props.$eventType === 'flower') return 'rgba(255, 192, 203, 0.2)';
    if (props.$eventType === 'circus') return 'rgba(255, 165, 0, 0.2)';
    if (props.$eventType === 'private') return 'rgba(128, 0, 128, 0.2)';
    return 'transparent';
  }};
  border-radius: 4px 4px 0 0;
  font-size: 0.7rem;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  &:hover {
    background-color: ${props => {
      if (props.$eventType === 'flower') return 'rgba(255, 192, 203, 0.3)';
      if (props.$eventType === 'circus') return 'rgba(255, 165, 0, 0.3)';
      if (props.$eventType === 'private') return 'rgba(128, 0, 128, 0.3)';
      return 'transparent';
    }};
  }
`;

const EventLink = styled.a`
  text-decoration: none;
  color: inherit;
  display: block;
  width: 100%;
`;

const DayNumber = styled.div`
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.primary};
  margin-top: 1.2rem;
`;

const DayStatus = styled.div`
  font-size: 0.9rem;
  padding: 0.3rem 0.5rem;
  border-radius: 4px;
  background-color: ${props => {
    if (props.$venue === 'castle') {
      return props.$isOpen ? 'rgba(139, 69, 19, 0.1)' : 'rgba(244, 67, 54, 0.1)';
    }
    if (props.$venue === 'gardens') {
      return props.$isOpen ? 'rgba(34, 139, 34, 0.1)' : 'rgba(34, 139, 34, 0.05)';
    }
    if (props.$venue === 'retail') {
      return props.$isOpen ? 'rgba(70, 130, 180, 0.1)' : 'rgba(70, 130, 180, 0.05)';
    }
    return 'transparent';
  }};
  color: ${props => {
    if (props.$venue === 'castle') {
      return props.$isOpen ? 'rgba(139, 69, 19, 0.8)' : 'rgba(244, 67, 54, 0.8)';
    }
    if (props.$venue === 'gardens') {
      return props.$isOpen ? 'rgba(34, 139, 34, 0.8)' : 'rgba(34, 139, 34, 0.5)';
    }
    if (props.$venue === 'retail') {
      return props.$isOpen ? 'rgba(70, 130, 180, 0.8)' : 'rgba(70, 130, 180, 0.5)';
    }
    return props.theme.colors.secondary;
  }};
  border: 1px solid ${props => {
    if (props.$venue === 'castle') {
      return props.$isOpen ? 'rgba(139, 69, 19, 0.3)' : 'rgba(244, 67, 54, 0.3)';
    }
    if (props.$venue === 'gardens') {
      return props.$isOpen ? 'rgba(34, 139, 34, 0.3)' : 'rgba(34, 139, 34, 0.1)';
    }
    if (props.$venue === 'retail') {
      return props.$isOpen ? 'rgba(70, 130, 180, 0.3)' : 'rgba(70, 130, 180, 0.1)';
    }
    return 'transparent';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const StatusIcon = styled(FontAwesomeIcon)`
  font-size: 0.8rem;
`;

const EventsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const EventsTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
  text-align: center;
`;

const EventsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const EventCard = styled.div`
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`;

const EventHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const EventIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.$eventType === 'flower' ? 'rgba(255, 192, 203, 0.2)' : 'rgba(255, 165, 0, 0.2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
`;

const EventTitleContainer = styled.div`
  flex: 1;
`;

const EventDate = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 0.3rem;
`;

const EventName = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin: 0;
`;

const EventDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
`;

const UpcomingDatesSection = styled.div`
  margin: 4rem 0;
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const UpcomingDatesTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const DatesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const DateCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 1.5rem;
  border-left: 4px solid ${props => {
    if (props.$type === 'christmas') return 'rgba(220, 20, 60, 0.8)';
    if (props.$type === 'closed') return 'rgba(244, 67, 54, 0.8)';
    if (props.$type === 'special') return 'rgba(255, 165, 0, 0.8)';
    return 'rgba(34, 139, 34, 0.8)';
  }};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const DateTitle = styled.h4`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DateRange = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 0.8rem;
`;

const DateDetails = styled.div`
  font-size: 1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 0.5rem;
`;

const DateNote = styled.div`
  font-size: 0.9rem;
  font-style: italic;
  color: ${props => props.theme.colors.secondary};
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid ${props => props.theme.colors.greyBackground};
`;

const OpeningTimes = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [viewMode, setViewMode] = useState('current'); // 'current', 'calendar', or 'events'
  const navigate = useNavigate();
  
  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    return () => clearInterval(timer);
  }, []);
  
  // Format current time
  const formattedTime = currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const currentDay = currentTime.toLocaleDateString('en-US', { weekday: 'long' });

  // Easter / spring half term 2026: castle open all week (overrides Tue/Wed pattern)
  const isSpringHalfTerm2026 = (date) =>
    date.getFullYear() === 2026 &&
    ((date.getMonth() === 2 && date.getDate() >= 30) ||
      (date.getMonth() === 3 && date.getDate() <= 10));
  
  // Get dynamic opening hours based on current date
  const getOpeningHours = () => {
    const currentDate = new Date();
    const day = currentDate.getDate();
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();

    // Castle closed until Christmas 2026 — Gardens, Playground & Retail Village still open
    if (isCastleClosedUntilChristmas(currentDate)) {
      const castleDisplay = getCastleOpeningDisplay({}, currentDate);
      return {
        castle: {
          days: castleDisplay.days,
          times: castleDisplay.times,
          note: castleDisplay.note
        },
        gardens: {
          days: "Mon - Sun",
          times: "09:30 - 17:00",
          note: "Still open: Gardens & Playground"
        },
        retail: {
          days: "Mon - Sun",
          times: "09:00 - 17:00",
          note: ""
        }
      };
    }
    
    // Christmas / Deck the Halls 2026: 14 Nov – 30 Dec
    if (year === 2026 && ((month === 10 && day >= 14) || (month === 11 && day <= 30))) {
      return {
        castle: {
          days: "Daily",
          times: "10:00 - 17:00",
          note: "Deck the Halls: 14 Nov – 30 Dec 2026"
        },
        gardens: {
          days: "Mon - Sun",
          times: "09:30 - 17:00",
          note: "Visitor Hub: 9:30am - 5pm"
        },
        retail: {
          days: "Mon - Sun",
          times: "09:00 - 17:00",
          note: ""
        }
      };
    }

    // Christmas period 2025 (historical): Nov 29 - Dec 30, 2025
    if (year === 2025 && month === 10 && day >= 29) {
      return {
        castle: {
          days: "Daily",
          times: "10:00 - 17:00",
          note: "Castle Christmas Experience: 29 Nov - 30 Dec"
        },
        gardens: {
          days: "Mon - Sun",
          times: "09:30 - 17:00",
          note: "Visitor Hub: 9:30am - 5pm"
        },
        retail: {
          days: "Mon - Sun",
          times: "09:00 - 17:00",
          note: ""
        }
      };
    }
    
    // 31 December 2026 — Castle closed after Deck the Halls; Gardens and Retail open
    if (year === 2026 && month === 11 && day === 31) {
      return {
        castle: {
          days: "Closed",
          times: "Closed",
          note: "Deck the Halls ended 30 Dec"
        },
        gardens: {
          days: "Daily",
          times: "09:30 - 17:00",
          note: ""
        },
        retail: {
          days: "Mon - Sun",
          times: "09:00 - 17:00",
          note: ""
        }
      };
    }

    // December 31st, 2025 - Castle closed, Gardens and Retail open
    if (year === 2025 && month === 11 && day === 31) {
      return {
        castle: {
          days: "Closed",
          times: "Closed",
          note: "Castle closed - reopens 19th March 2026"
        },
        gardens: {
          days: "Daily",
          times: "09:30 - 17:00",
          note: ""
        },
        retail: {
          days: "Mon - Sun",
          times: "09:00 - 17:00",
          note: ""
        }
      };
    }
    
    // January 2nd to March 18, 2026 - Castle closed, Gardens & Retail open daily
    if (year === 2026 && ((month === 0 && day >= 2) || month === 1 || (month === 2 && day < 19))) {
      return {
        castle: {
          days: "Closed",
          times: "Closed",
          note: "Castle reopens 19th March 2026"
        },
        gardens: {
          days: "Daily",
          times: "09:30 - 16:00",
          note: "Gardens & Playground only"
        },
        retail: {
          days: "Daily",
          times: "09:00 - 17:00",
          note: ""
        }
      };
    }
    
    // March 19, 2026 onwards — Castle closed Tuesday & Wednesday
    // (Christmas / Deck the Halls daily opening is handled above)
    if (year === 2026 && (month > 2 || (month === 2 && day >= 19))) {
      const castleDisplay = getCastleOpeningDisplay({}, currentDate);
      return {
        castle: {
          days: castleDisplay.days,
          times: castleDisplay.times,
          note: castleDisplay.note
        },
        gardens: {
          days: "Mon - Sun",
          times: "09:30 - 17:00",
          note: "Visitor Hub: 9:30am - 5pm"
        },
        retail: {
          days: "Mon - Sun",
          times: "09:00 - 17:00",
          note: ""
        }
      };
    }

    // 2025 before Christmas season (through 28 Nov): Castle closed Tuesday & Wednesday
    if (year === 2025 && (month < 10 || (month === 10 && day < 29))) {
      return {
        castle: {
          days: "Mon, Thu - Sun",
          times: "10:00 - 17:00",
          note: "Closed Tuesday and Wednesday · Last entry 16:00"
        },
        gardens: {
          days: "Mon - Sun",
          times: "09:30 - 17:00",
          note: ""
        },
        retail: {
          days: "Mon - Sun",
          times: "09:00 - 17:00",
          note: ""
        }
      };
    }

    // Default (e.g. 2027+): Castle closed Tuesday & Wednesday
    return {
      castle: {
        days: "Mon, Thu - Sun",
        times: "10:00 - 17:00",
        note: "Closed Tuesday and Wednesday · Last entry 16:00"
      },
      gardens: {
        days: "Mon - Sun",
        times: "09:30 - 17:00",
        note: ""
      },
      retail: {
        days: "Mon - Sun",
        times: "09:00 - 17:00",
        note: ""
      }
    };
  };
  
  const openingHours = getOpeningHours();
  const animalParkDisplay = getAnimalParkOpeningDisplay();
  if (openingHours.retail) {
    const animalParkNote =
      animalParkDisplay.days === 'Daily'
        ? `Animal Park: open daily ${ANIMAL_PARK_OCTOBER_HALF_TERM_2026.label}, ${animalParkDisplay.times}`
        : `Animal Park: Sat–Sun ${animalParkDisplay.times} · daily ${ANIMAL_PARK_OCTOBER_HALF_TERM_2026.label}`;
    openingHours.retail.note = openingHours.retail.note
      ? `${openingHours.retail.note} · ${animalParkNote}`
      : animalParkNote;
  }

  // Function to check if a venue is currently open or opening soon
  const isVenueOpen = (venue, venueType) => {
    if (!venue || !venue.times) return { isOpen: false, openingSoon: false };
    
    // Get current date
    const currentDate = new Date();
    const day = currentDate.getDate();
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Check special closure dates
    if (year === 2025) {
      // Christmas Day - All closed
      if (month === 11 && day === 25) {
        return { isOpen: false, openingSoon: false };
      }
      
      // Boxing Day (Dec 26) - Gardens and Playground only, 9:30am-3:30pm
      if (month === 11 && day === 26) {
        if (venueType === 'castle' || venueType === 'retail') {
          return { isOpen: false, openingSoon: false };
        }
        if (venueType === 'gardens') {
          // Check if within Boxing Day hours (9:30am-3:30pm)
          const timeRange = venue.times.split('-').map(t => t.trim());
          if (timeRange.length === 2) {
            const [openHour, openMinute] = timeRange[0].split(':').map(Number);
            const [closeHour, closeMinute] = timeRange[1].split(':').map(Number);
            const openTime = new Date();
            openTime.setHours(9, 30, 0);
            const closeTime = new Date();
            closeTime.setHours(15, 30, 0);
            const isOpen = currentTime >= openTime && currentTime <= closeTime;
            const oneHourBeforeOpen = new Date(openTime.getTime() - 60 * 60000);
            const openingSoon = currentTime >= oneHourBeforeOpen && currentTime < openTime;
            return { isOpen, openingSoon };
          }
        }
      }
      
      // December 31st, 2025 - Castle closed, Gardens and Retail open
      if (month === 11 && day === 31) {
        if (venueType === 'castle') {
          return { isOpen: false, openingSoon: false };
        }
        // Gardens and Retail open
        return { isOpen: true, openingSoon: false };
      }

      // Castle specific dates
      if (venueType === 'castle') {
        // Castle closed until November 29th, 2025
        if (month < 10 || (month === 10 && day < 29)) { // Before November 29th
          return { isOpen: false, openingSoon: false };
        }
        
        // Castle Christmas Experience: Nov 29 - Dec 30, 2025 (10am-5pm daily)
        if (month === 10 && day >= 29) { // November 29th onwards
          // Use Christmas hours (10am-5pm)
          const timeRange = venue.times.split('-').map(t => t.trim());
          if (timeRange.length === 2) {
            const [openHour, openMinute] = timeRange[0].split(':').map(Number);
            const [closeHour, closeMinute] = timeRange[1].split(':').map(Number);
            const openTime = new Date();
            openTime.setHours(openHour, openMinute || 0, 0);
            const closeTime = new Date();
            closeTime.setHours(closeHour, closeMinute || 0, 0);
            const isOpen = currentTime >= openTime && currentTime <= closeTime;
            const oneHourBeforeOpen = new Date(openTime.getTime() - 60 * 60000);
            const openingSoon = currentTime >= oneHourBeforeOpen && currentTime < openTime;
            return { isOpen, openingSoon };
          }
        }
        
        // April private event (24th-27th) - Castle closed
        if (month === 3 && (day >= 24 && day <= 27)) {
          return { isOpen: false, openingSoon: false };
        }
        
        // April 29th and 30th - Castle closed
        if (month === 3 && (day === 29 || day === 30)) {
          return { isOpen: false, openingSoon: false };
        }
        
        // May closures
        if (month === 4) { // May
          // Tuesday and Wednesday closures
          if (dayOfWeek === 2 || dayOfWeek === 3) return { isOpen: false, openingSoon: false };
          // Specific dates
          if (day === 6 || day === 7 || day === 13 || day === 14 || day === 20 || day === 21 || day === 27 || day === 28) {
            return { isOpen: false, openingSoon: false };
          }
        }
        // June closures
        if (month === 5) { // June
          if (dayOfWeek === 2 || dayOfWeek === 3) return { isOpen: false, openingSoon: false };
          // Private family event closure: June 16th-23rd, 2025
          if (day >= 16 && day <= 23) {
            return { isOpen: false, openingSoon: false };
          }
        }
        // September closures
        if (month === 8) { // September
          if (dayOfWeek === 2 || dayOfWeek === 3) return { isOpen: false, openingSoon: false };
        }
        // July 20th, 2025 - Castle closed
        if (month === 6 && day === 20) { // July
          return { isOpen: false, openingSoon: false };
        }
        // October closures - Tuesdays and Wednesdays
        if (month === 9) { // October
          if (dayOfWeek === 2 || dayOfWeek === 3) return { isOpen: false, openingSoon: false };
        }
      }

      // Gardens specific closures
      if (venueType === 'gardens') {
        // Private family event closure: June 16th-23rd, 2025
        if (month === 5 && day >= 16 && day <= 23) { // June
          return { isOpen: false, openingSoon: false };
        }
        // July 20th, 2025 - Gardens closed
        if (month === 6 && day === 20) { // July
          return { isOpen: false, openingSoon: false };
        }
      }
    }
    
    // Check 2026 dates
    if (year === 2026) {
      // New Year's Day (Jan 1) - Gardens and Playground only, 9:30am-3:30pm
      if (month === 0 && day === 1) {
        if (venueType === 'castle' || venueType === 'retail') {
          return { isOpen: false, openingSoon: false };
        }
        if (venueType === 'gardens') {
          // Check if within New Year's Day hours (9:30am-3:30pm)
          const openTime = new Date();
          openTime.setHours(9, 30, 0);
          const closeTime = new Date();
          closeTime.setHours(15, 30, 0);
          const isOpen = currentTime >= openTime && currentTime <= closeTime;
          const oneHourBeforeOpen = new Date(openTime.getTime() - 60 * 60000);
          const openingSoon = currentTime >= oneHourBeforeOpen && currentTime < openTime;
          return { isOpen, openingSoon };
        }
      }
      
      // January 2nd to March 18, 2026 - Castle closed, Gardens & Retail open daily
      if ((month === 0 && day >= 2) || month === 1 || (month === 2 && day < 19)) {
        if (venueType === 'castle') {
          return { isOpen: false, openingSoon: false };
        }
        if (venueType === 'gardens') {
          // Use winter hours (9:30am-4pm)
          const openTime = new Date();
          openTime.setHours(9, 30, 0);
          const closeTime = new Date();
          closeTime.setHours(16, 0, 0);
          const isOpen = currentTime >= openTime && currentTime <= closeTime;
          const oneHourBeforeOpen = new Date(openTime.getTime() - 60 * 60000);
          const openingSoon = currentTime >= oneHourBeforeOpen && currentTime < openTime;
          return { isOpen, openingSoon };
        }
        // Retail open daily
        if (venueType === 'retail') {
          return { isOpen: true, openingSoon: false };
        }
      }
      
      // Castle closed Tuesday & Wednesday from March 19, 2026 onwards
      // (Christmas / Deck the Halls daily opening is handled by isCastleClosedTuesdayOrWednesday)
    }

    if (venueType === 'castle' && isCastleClosedUntilChristmas(currentDate)) {
      return { isOpen: false, openingSoon: false };
    }

    if (venueType === 'castle' && isCastleClosedTuesdayOrWednesday(currentDate)) {
      return { isOpen: false, openingSoon: false };
    }
    
    // Parse opening hours
    const timeRange = venue.times.split('-').map(t => t.trim());
    if (timeRange.length !== 2) return { isOpen: false, openingSoon: false };
    
    // Convert times to 24-hour format for comparison
    const [openHour, openMinute] = timeRange[0].split(':').map(Number);
    const [closeHour, closeMinute] = timeRange[1].split(':').map(Number);
    
    const openTime = new Date();
    openTime.setHours(openHour, openMinute || 0, 0);
    
    const closeTime = new Date();
    closeTime.setHours(closeHour, closeMinute || 0, 0);
    
    // Check if current time is between open and close times
    const isOpen = currentTime >= openTime && currentTime <= closeTime;
    
    // Check if opening within 1 hour
    const oneHourBeforeOpen = new Date(openTime.getTime() - 60 * 60000);
    const openingSoon = currentTime >= oneHourBeforeOpen && currentTime < openTime;
    
    return { isOpen, openingSoon };
  };
  
  // Check if each venue is open or opening soon
  const castleStatus = isVenueOpen(openingHours.castle, 'castle');
  const gardensStatus = isVenueOpen(openingHours.gardens, 'gardens');
  const retailStatus = isVenueOpen(openingHours.retail, 'retail');
  
  const renderCalendar = () => {
    const months = [
      { name: 'April 2025', days: 30, startDay: 1 }, // April 1st is Tuesday
      { name: 'May 2025', days: 31, startDay: 4 },   // May 1st is Thursday
      { name: 'June 2025', days: 30, startDay: 0 },  // June 1st is Sunday
      { name: 'July 2025', days: 31, startDay: 2 },  // July 1st is Tuesday
      { name: 'August 2025', days: 31, startDay: 5 }, // August 1st is Friday
      { name: 'September 2025', days: 30, startDay: 1 }, // September 1st is Monday
      { name: 'October 2025', days: 31, startDay: 3 }, // October 1st is Wednesday
      { name: 'November 2025', days: 30, startDay: 6 }, // November 1st is Saturday
      { name: 'December 2025', days: 31, startDay: 1 }, // December 1st is Monday
      { name: 'January 2026', days: 31, startDay: 4 }, // January 1st is Thursday
      { name: 'February 2026', days: 28, startDay: 0 }, // February 1st is Sunday
      { name: 'March 2026', days: 31, startDay: 0 } // March 1st is Sunday
    ];

    const getStartDay = (year, month) => {
      const firstDay = new Date(year, month, 1);
      return firstDay.getDay() || 7; // Convert Sunday (0) to 7
    };

    const isSpecialEvent = (month, day) => {
      // April Circus (18th-21st)
      if (month === 3 && (day === 18 || day === 19 || day === 20 || day === 21)) {
        return { isEvent: true, type: 'circus', text: '🎪 Circus' };
      }
      // April Private Event (24th-27th)
      if (month === 3 && (day >= 24 && day <= 27)) {
        return { isEvent: true, type: 'private', text: 'Private Event' };
      }
      // July Flower Show
      if (month === 6 && (day === 12 || day === 13)) {
        return { isEvent: true, type: 'flower', text: '🌸 Flower Show' };
      }
      return { isEvent: false };
    };

    const getOpeningStatus = (date) => {
      const day = date.getDate();
      const month = date.getMonth();
      const year = date.getFullYear();
      const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.

      // Christmas Day - All closed
      if (year === 2025 && month === 11 && day === 25) {
        return { castle: false, gardens: false, retail: false };
      }
      
      // Boxing Day (Dec 26, 2025) - Gardens and Playground only, 9:30am-3:30pm
      if (year === 2025 && month === 11 && day === 26) {
        return { castle: false, gardens: true, retail: false };
      }
      
      // December 31st, 2025 - Castle closed, Gardens and Retail open
      if (year === 2025 && month === 11 && day === 31) {
        return { castle: false, gardens: true, retail: true };
      }
      
      // New Year's Day (Jan 1, 2026) - Gardens and Playground only, 9:30am-3:30pm
      if (year === 2026 && month === 0 && day === 1) {
        return { castle: false, gardens: true, retail: false };
      }

      // Castle specific dates
      if (year === 2025) {
        // Castle closed until November 29th, 2025
        if (month < 10 || (month === 10 && day < 29)) { // Before November 29th
          return { castle: false, gardens: true, retail: true };
        }
        
        // Castle Christmas Experience: Nov 29 - Dec 30, 2025 (10am-5pm daily)
        if (month === 10 && day >= 29) { // November 29th onwards
          return { castle: true, gardens: true, retail: true };
        }
        
        // December 1-30, 2025 - Castle Christmas Experience
        if (month === 11 && day >= 1 && day <= 30) {
          return { castle: true, gardens: true, retail: true };
        }
        
        // December 31st, 2025 - Castle closed
        if (month === 11 && day === 31) {
          return { castle: false, gardens: true, retail: true };
        }
        
        // April private event (24th-27th) - Castle closed
        if (month === 3 && (day >= 24 && day <= 27)) {
          return { castle: false, gardens: true, retail: true };
        }
        
        // April 29th and 30th - Castle closed
        if (month === 3 && (day === 29 || day === 30)) {
          return { castle: false, gardens: true, retail: true };
        }
        
        // May closures
        if (month === 4) { // May
          // Tuesday and Wednesday closures
          if (dayOfWeek === 2 || dayOfWeek === 3) return { castle: false, gardens: true, retail: true };
          // Specific dates
          if (day === 6 || day === 7 || day === 13 || day === 14 || day === 20 || day === 21 || day === 27 || day === 28) {
            return { castle: false, gardens: true, retail: true };
          }
        }
        // June closures
        if (month === 5) { // June
          if (dayOfWeek === 2 || dayOfWeek === 3) return { castle: false, gardens: true, retail: true };
          // Private family event closure: June 16th-23rd, 2025
          if (day >= 16 && day <= 23) {
            if (day === 22) return { castle: false, gardens: false, retail: false }; // June 22nd - all closed
            return { castle: false, gardens: false, retail: true }; // June 16-21, 23 - retail open
          }
        }
        // September closures
        if (month === 8) { // September
          if (dayOfWeek === 2 || dayOfWeek === 3) return { castle: false, gardens: true, retail: true };
        }
        // July 20th, 2025 - Castle and Gardens closed
        if (month === 6 && day === 20) { // July
          return { castle: false, gardens: false, retail: true };
        }
        // October closures - Tuesdays and Wednesdays
        if (month === 9) { // October
          if (dayOfWeek === 2 || dayOfWeek === 3) return { castle: false, gardens: true, retail: true };
        }
      }
      
      // 2026 dates
      if (year === 2026) {
        // January 2nd to March 18, 2026 - Castle closed, Gardens & Retail open daily
        if ((month === 0 && day >= 2) || month === 1 || (month === 2 && day < 19)) {
          return { castle: false, gardens: true, retail: true };
        }

        // Castle closed until Christmas 2026 - Gardens & Retail still open
        if (isCastleClosedUntilChristmas(date)) {
          return { castle: false, gardens: true, retail: true };
        }

        // Deck the Halls Christmas 2026: 14 Nov – 30 Dec
        if ((month === 10 && day >= 14) || (month === 11 && day <= 30)) {
          return { castle: true, gardens: true, retail: true };
        }

        // 31 December 2026 — Castle closed after Deck the Halls
        if (month === 11 && day === 31) {
          return { castle: false, gardens: true, retail: true };
        }

        // March 19, 2026 onwards – Castle closed Tuesday & Wednesday
        // (Christmas / Deck the Halls daily opening is handled above)
        if (isCastleClosedTuesdayOrWednesday(date)) {
          return { castle: false, gardens: true, retail: true };
        }
        return { castle: true, gardens: true, retail: true };
      }

      // Default opening times — Castle closed Tuesday & Wednesday
      if (isCastleClosedTuesdayOrWednesday(date)) {
        return { castle: false, gardens: true, retail: true };
      }
      return { castle: true, gardens: true, retail: true };
    };

    return (
      <CalendarContainer>
        {months.map((month, monthIndex) => {
          const days = [];
          const actualStartDay = getStartDay(2025, monthIndex + 3);
          // Add empty cells for days before the start of the month
          for (let i = 0; i < actualStartDay - 1; i++) {
            days.push(<DayCell key={`empty-${i}`} />);
          }
          
          // Add days of the month
          for (let day = 1; day <= month.days; day++) {
            // Calculate correct year and month
            let year, monthNum;
            if (monthIndex < 9) {
              // April - November 2025
              year = 2025;
              monthNum = monthIndex + 3; // 3 = April
            } else if (monthIndex === 9) {
              // December 2025
              year = 2025;
              monthNum = 11;
            } else if (monthIndex === 10) {
              // January 2026
              year = 2026;
              monthNum = 0;
            } else if (monthIndex === 11) {
              // February 2026
              year = 2026;
              monthNum = 1;
            } else if (monthIndex === 12) {
              // March 2026
              year = 2026;
              monthNum = 2;
            }
            
            const date = new Date(year, monthNum, day);
            const status = getOpeningStatus(date);
            const specialEvent = isSpecialEvent(monthNum, day);
            
            days.push(
              <DayCell key={`day-${day}`}>
                {specialEvent.isEvent && (
                  <EventLink href="/whats-on/events">
                    <EventIndicator $eventType={specialEvent.type}>
                      {specialEvent.text}
                    </EventIndicator>
                  </EventLink>
                )}
                <DayNumber>{day}</DayNumber>
                <DayStatus $venue="castle" $isOpen={status.castle}>
                  <StatusIcon icon={status.castle ? faCheck : faTimes} />
                  Castle: {status.castle ? 'Open' : 'Closed'}
                </DayStatus>
                <DayStatus $venue="gardens" $isOpen={status.gardens}>
                  <StatusIcon icon={status.gardens ? faCheck : faTimes} />
                  Gardens: {status.gardens ? 'Open' : 'Closed'}
                </DayStatus>
                <DayStatus $venue="retail" $isOpen={status.retail}>
                  <StatusIcon icon={status.retail ? faCheck : faTimes} />
                  Retail: {status.retail ? 'Open' : 'Closed'}
                </DayStatus>
              </DayCell>
            );
          }
          
          return (
            <MonthContainer key={month.name}>
              <MonthTitle>{month.name}</MonthTitle>
              <CalendarGrid>
                <DayHeader>Mon</DayHeader>
                <DayHeader>Tue</DayHeader>
                <DayHeader>Wed</DayHeader>
                <DayHeader>Thu</DayHeader>
                <DayHeader>Fri</DayHeader>
                <DayHeader>Sat</DayHeader>
                <DayHeader>Sun</DayHeader>
                {days}
              </CalendarGrid>
            </MonthContainer>
          );
        })}
      </CalendarContainer>
    );
  };

  return (
    <OpeningTimesContainer>
      <SEO 
        title="Belvoir Castle Opening Times & Visitor Information"
        description="Plan your visit to Belvoir Castle with our comprehensive opening times and visitor information. Check seasonal opening hours, special events, and admission details."
        keywords="Belvoir Castle opening times, visitor information, castle hours, admission times, Leicestershire attractions, castle visiting hours"
        canonicalUrl="https://www.belvoircastle.com/visit/opening-times"
        ogImage={castleImage}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "TouristAttraction",
          "name": "Belvoir Castle",
          "description": "Historic castle and gardens in Leicestershire, open to visitors throughout the year.",
          "image": castleImage,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Belvoir Castle",
            "addressLocality": "Grantham",
            "addressRegion": "Leicestershire",
            "postalCode": "NG32 1PE",
            "addressCountry": "UK"
          },
          "openingHoursSpecification": isCastleClosedUntilChristmas()
            ? {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
                "opens": "09:30",
                "closes": "17:00",
                "description": "Castle closed until Christmas (re-opens 14 November 2026). Gardens and Playground open daily."
              }
            : {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": isCastleOpenAllDaysThisWeek()
                  ? ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
                  : ["Monday", "Thursday", "Friday", "Saturday", "Sunday"],
                "opens": "10:00",
                "closes": "17:00"
              },
          "priceRange": "££",
          "telephone": "+441476871001",
          "url": "https://www.belvoircastle.com"
        }}
      />
      <ParallaxBanner 
        title="Opening Times"
        subtitle="Plan Your Visit"
        image={castleImage}
      />
      
      <ContentContainer>
        <ViewToggle>
          <ToggleButton 
            $active={viewMode === 'current'} 
            onClick={() => setViewMode('current')}
          >
            <FontAwesomeIcon icon={faClock} />
            Current View
          </ToggleButton>
          <EventsLink onClick={() => navigate('/whats-on/events')}>
            <FontAwesomeIcon icon={faCalendarAlt} />
            View Events
          </EventsLink>
        </ViewToggle>

        {viewMode === 'current' ? (
          <>
            <SectionTitle>Our Opening Hours</SectionTitle>
            <IntroText>
              Belvoir Castle welcomes visitors throughout the year. Please check our opening times below before planning your visit.
              Different areas of the estate have varying opening hours to ensure you have the best experience.
            </IntroText>
            
            {isCastleClosedUntilChristmas(currentTime) && (
              <OpeningPageNotice>
                <h3>Castle Closed – Re-opens at Christmas</h3>
                <p>
                  Belvoir Castle is currently closed and re-opens for Christmas on <strong>14 November 2026</strong>.
                </p>
                <p>
                  <strong>The Gardens and Adventure Playground are still open</strong>, as is the Retail Village.
                </p>
              </OpeningPageNotice>
            )}

            <CurrentTimeDisplay>
              Current Time: {formattedTime} on {currentDay}
            </CurrentTimeDisplay>
            
            {/* <UpcomingDatesSection>
              <UpcomingDatesTitle>
                <FontAwesomeIcon icon={faCalendarAlt} />
                Upcoming Important Dates
              </UpcomingDatesTitle>
              <DatesList>
                <DateCard $type="christmas">
                  <DateTitle>
                    <FontAwesomeIcon icon={faCalendar} />
                    Castle Christmas Experience
                  </DateTitle>
                  <DateRange>Saturday 29th November - Tuesday 30th December 2025</DateRange>
                  <DateDetails>
                    <strong>Castle:</strong> 10:00am - 5:00pm daily<br/>
                    <strong>Visitor Hub:</strong> 9:30am - 5:00pm<br/>
                    <strong>Gardens & Playground:</strong> 9:30am - 5:00pm<br/>
                    <strong>Retail Village:</strong> 9:00am - 5:00pm
                  </DateDetails>
                </DateCard>
                
                <DateCard $type="closed">
                  <DateTitle>
                    <FontAwesomeIcon icon={faTimes} />
                    Christmas Day
                  </DateTitle>
                  <DateRange>Thursday 25th December 2025</DateRange>
                  <DateDetails>
                    <strong>All areas closed</strong>
                  </DateDetails>
                  <DateNote>We will be closed for Christmas Day</DateNote>
                </DateCard>
                
                <DateCard $type="special">
                  <DateTitle>
                    <FontAwesomeIcon icon={faInfoCircle} />
                    Boxing Day
                  </DateTitle>
                  <DateRange>Friday 26th December 2025</DateRange>
                  <DateDetails>
                    <strong>Gardens & Playground:</strong> 9:30am - 3:30pm<br/>
                    <strong>Castle:</strong> Closed<br/>
                    <strong>Retail Village:</strong> Closed<br/>
                    <strong>Farm Shop & Duchess Collection:</strong> 9:00am - 3:30pm
                  </DateDetails>
                </DateCard>
                
                <DateCard $type="closed">
                  <DateTitle>
                    <FontAwesomeIcon icon={faTimes} />
                    New Year's Eve
                  </DateTitle>
                  <DateRange>Wednesday 31st December 2025</DateRange>
                  <DateDetails>
                    <strong>Castle:</strong> Closed<br/>
                    <strong>Animal Park:</strong> Closed<br/>
                    <strong>Gardens & Playground:</strong> 9:30am - 5:00pm<br/>
                    <strong>Retail Village:</strong> 9:00am - 5:00pm
                  </DateDetails>
                  <DateNote>Castle closed until 14th March 2026</DateNote>
                </DateCard>
                
                <DateCard $type="special">
                  <DateTitle>
                    <FontAwesomeIcon icon={faInfoCircle} />
                    New Year's Day
                  </DateTitle>
                  <DateRange>Thursday 1st January 2026</DateRange>
                  <DateDetails>
                    <strong>Gardens & Playground:</strong> 9:30am - 3:30pm<br/>
                    <strong>Castle:</strong> Closed<br/>
                    <strong>Retail Village:</strong> Closed<br/>
                    <strong>Farm Shop & Duchess Collection:</strong> 9:00am - 3:30pm
                  </DateDetails>
                </DateCard>
                
                <DateCard $type="special">
                  <DateTitle>
                    <FontAwesomeIcon icon={faInfoCircle} />
                    Winter Period
                  </DateTitle>
                  <DateRange>2nd January - 14th March 2026</DateRange>
                  <DateDetails>
                    <strong>Gardens & Playground:</strong> 9:30am - 4:00pm daily<br/>
                    <strong>Castle:</strong> Closed<br/>
                    <strong>Retail Village:</strong> 9:00am - 5:00pm daily<br/>
                    <strong>Animal Park:</strong> Closed<br/>
                    <strong>Farm Shop & Duchess Collection:</strong> 9:00am - 5:00pm
                  </DateDetails>
                  <DateNote>Castle reopens on 14th March 2026 (Mother's Day Weekend)</DateNote>
                </DateCard>
                
                <DateCard $type="special">
                  <DateTitle>
                    <FontAwesomeIcon icon={faCheck} />
                    Castle Reopens
                  </DateTitle>
                  <DateRange>Saturday 14th March 2026 onwards</DateRange>
                  <DateDetails>
                    <strong>Castle:</strong> 10:00am - 5:00pm daily<br/>
                    <strong>Visitor Hub:</strong> 9:30am - 5:00pm<br/>
                    <strong>Gardens & Playground:</strong> 9:30am - 5:00pm<br/>
                    <strong>Retail Village:</strong> 9:00am - 5:00pm
                  </DateDetails>
                  
                </DateCard>
              </DatesList>
            </UpcomingDatesSection> */}
            
            <TimesGrid>
              <VenueCard>
                <VenueImage image={castleImage}>
                  <StatusBadge $isOpen={castleStatus.isOpen} $openingSoon={castleStatus.openingSoon}>
                    <FontAwesomeIcon icon={faClock} />
                    {castleStatus.isOpen ? 'Open Now' : castleStatus.openingSoon ? 'Opening Soon' : 'Closed'}
                  </StatusBadge>
                </VenueImage>
                <VenueContent>
                  <VenueName>
                    Castle
                  </VenueName>
                  <TimeInfo>
                    <DayLabel>{openingHours.castle?.days || "Mon, Thu - Sun"}</DayLabel>
                    <Hours>{openingHours.castle?.times || "10:00 - 17:00"}</Hours>
                    {(openingHours.castle?.note) && 
                      <Note>{openingHours.castle?.note}</Note>}
                  </TimeInfo>
                  <VenueDescription>
                    Explore the magnificent state rooms and discover centuries of history within the walls of Belvoir Castle.
                    Our knowledgeable guides are available throughout the castle to enhance your experience.
                  </VenueDescription>
                </VenueContent>
              </VenueCard>
              
              <VenueCard>
                <VenueImage image={gardensImage}>
                  <StatusBadge $isOpen={gardensStatus.isOpen} $openingSoon={gardensStatus.openingSoon}>
                    <FontAwesomeIcon icon={faClock} />
                    {gardensStatus.isOpen ? 'Open Now' : gardensStatus.openingSoon ? 'Opening Soon' : 'Closed'}
                  </StatusBadge>
                </VenueImage>
                <VenueContent>
                  <VenueName>
                    Gardens & Playground
                  </VenueName>
                  <TimeInfo>
                    <DayLabel>{openingHours.gardens?.days || "Mon - Sun"}</DayLabel>
                    <Hours>{openingHours.gardens?.times || "10:00 - 17:00"}</Hours>
                    {(openingHours.gardens?.note ) && 
                      <Note>{openingHours.gardens?.note}</Note>}
                  </TimeInfo>
                  <VenueDescription>
                    Wander through our beautifully landscaped gardens and let the children enjoy our adventure playground.
                    The gardens offer stunning views across the Vale of Belvoir in every season.
                  </VenueDescription>
                </VenueContent>
              </VenueCard>
              
              <VenueCard>
                <VenueImage image={retailImage}>
                  <StatusBadge $isOpen={retailStatus.isOpen} $openingSoon={retailStatus.openingSoon}>
                    <FontAwesomeIcon icon={faClock} />
                    {retailStatus.isOpen ? 'Open Now' : retailStatus.openingSoon ? 'Opening Soon' : 'Closed'}
                  </StatusBadge>
                </VenueImage>
                <VenueContent>
                  <VenueName>
                    Retail Village
                  </VenueName>
                  <TimeInfo>
                    <DayLabel>{openingHours.retail?.days || "Mon - Sun"}</DayLabel>
                    <Hours>{openingHours.retail?.times || "09:30 - 17:00"}</Hours>
                    {openingHours.retail?.note && <Note>{openingHours.retail.note}</Note>}
                  </TimeInfo>
                  <VenueDescription>
                    Visit our retail village for unique gifts, local produce, and souvenirs. Our shops offer a range of
                    products including homeware, food and drink, and Belvoir Castle merchandise.
                  </VenueDescription>
                </VenueContent>
              </VenueCard>
            </TimesGrid>
            
            <CTASection>
              <CTACard>
                <CTATitle>Visit the Castle</CTATitle>
                <CTADescription>
                  Experience the majesty of our historic castle and beautiful state rooms with a guided tour.
                </CTADescription>
                <CTAButton href="https://online1.venpos.net/VenPosJourneyUI/WebSaleItems?id=b0f1a62a-a34a-4c4e-a88d-6fda03a19279&type=WebPage&locationId=978&mainWebPageId=e39e1d45-8941-4502-8c92-bb7082a9ddbf&startId=e39e1d45-8941-4502-8c92-bb7082a9ddbf">
                  Book Castle Tickets
                </CTAButton>
              </CTACard>
              
              <CTACard>
                <CTATitle>Explore the Gardens</CTATitle>
                <CTADescription>
                  Discover our beautiful gardens and let the children enjoy our adventure playground.
                </CTADescription>
                <CTAButton href="https://online1.venpos.net/VenPosJourneyUI/WebSaleItems?id=40ca044f-534f-4b7f-83ea-b4156f0633d3&type=WebPage&locationId=978&mainWebPageId=b0f1a62a-a34a-4c4e-a88d-6fda03a19279&startId=e39e1d45-8941-4502-8c92-bb7082a9ddbf">
                  Book Garden Tickets
                </CTAButton>
              </CTACard>
            </CTASection>
            
            <div style={{ marginTop: '4rem' }}>
              <SectionTitle>Additional Information</SectionTitle>
              <IntroText>
                Please note that last entry to all areas is typically 1 hour before closing time. The castle may occasionally
                close for private events or maintenance - please check our events calendar before planning your visit.
                Dogs are welcome in the gardens but must be kept on a lead at all times.
              </IntroText>
            </div>
          </>
        ) : (
          renderCalendar()
        )}
      </ContentContainer>
      
      <Footer />
    </OpeningTimesContainer>
  );
};

export default OpeningTimes; 